// Authentication utilities for token validation and management

/**
 * Validates if the current token is still valid by making a request to the backend
 * @returns {Promise<boolean>} - true if token is valid, false otherwise
 */
export const validateToken = async () => {
  const token = sessionStorage.getItem('access_token');
  
  if (!token) {
    return false;
  }

  try {
    const response = await fetch('/api/user/profile', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    // If we get a 401, the token is invalid/expired
    if (response.status === 401) {
      return false;
    }

    // If we get any other error, we'll assume the token is still valid
    // to avoid false positives due to network issues
    return response.ok;
  } catch (error) {
    console.warn('Token validation failed due to network error:', error);
    // On network errors, assume token is still valid to avoid false positives
    return true;
  }
};

/**
 * Clears the authentication token and redirects to login
 */
export const clearAuthAndRedirect = () => {
  sessionStorage.removeItem('access_token');
  // Use window.location.href to ensure a full page reload
  window.location.href = '/';
};

/**
 * Checks if user is authenticated with a valid token
 * @returns {Promise<boolean>} - true if authenticated with valid token
 */
export const isAuthenticated = async () => {
  const token = sessionStorage.getItem('access_token');
  
  if (!token) {
    return false;
  }

  return await validateToken();
};

/**
 * Handles authentication errors by clearing token and redirecting
 * @param {Response} response - The failed response
 * @param {Function} navigate - React Router navigate function (optional)
 */
export const handleAuthError = (response, navigate = null) => {
  if (response.status === 401) {
    console.log('Authentication failed - token expired or invalid');
    clearAuthAndRedirect();
  }
};

/**
 * Handles API errors and sets appropriate error state
 * @param {Response} response - The failed response
 * @param {Function} setError - Function to set error message
 * @param {Function} setIsAuthError - Function to set auth error flag
 * @param {Function} setLoading - Function to set loading state
 * @param {Function} getTranslation - Translation function
 * @param {string} language - Current language
 * @returns {boolean} - Returns true if error was handled, false otherwise
 */
export const handleApiError = async (response, setError, setIsAuthError, setLoading, getTranslation, language) => {
  if (response.status === 401) {
    setIsAuthError(true);
    setError(getTranslation('brakTokenuAutoryzacji', language));
    setLoading(false);
    handleAuthError(response);
    return true;
  }
  
  let errorText;
  try {
    errorText = await response.text();
  } catch (e) {
    errorText = getTranslation('nieMoznaOdczytaćOdpowiedzi', language);
  }
  
  setIsAuthError(false);
  setError(`${getTranslation('bladLadowania', language)} ${response.status}: ${errorText}`);
  setLoading(false);
  return false;
};